import { APPConfig } from 'config/appConfig'

/** Aligns with ns-share-api validateSponsorId — e.g. UST3021686, CAT3021686 */
export const SPONSOR_ID_PATTERN = /^[a-zA-Z0-9]+$/

/** Mysite shop subdomain segments from sponsor API shopUrl */
export const MYSITE_SUBDOMAIN_PATTERN = /^[a-zA-Z0-9_-]+$/

export const PITCH_ID_PATTERN = /^[a-zA-Z0-9]+$/

const STATIC_TRUSTED_REDIRECT_HOSTS = [
  'nuskin.com',
  'mynuskin.com',
  'equinox.shop',
  'nskn.co',
  'nsk.n.co',
]

const escapeRegex = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Config-driven + static Nu Skin trusted redirect hosts.
 */
export const getTrustedRedirectHosts = () => {
  const configDomain = APPConfig?.getAppConfig()?.domain
  const hosts = [...STATIC_TRUSTED_REDIRECT_HOSTS]
  if (configDomain && typeof configDomain === 'string') {
    hosts.push(configDomain.toLowerCase())
  }
  return [...new Set(hosts)]
}

const isHostnameTrusted = hostname => {
  const host = (hostname || '').toLowerCase()
  if (!host) return false
  return getTrustedRedirectHosts().some(trusted => {
    const normalized = trusted.toLowerCase()
    return host === normalized || host.endsWith(`.${normalized}`)
  })
}

/**
 * Returns true when url is same-origin or on a trusted Nu Skin host.
 */
export const isTrustedRedirectUrl = url => {
  if (!url || typeof url !== 'string') return false
  const trimmed = url.trim()
  if (!trimmed || trimmed.startsWith('//')) return false
  if (/^(javascript|data|vbscript):/i.test(trimmed)) return false

  try {
    const parsed = new URL(trimmed, typeof window !== 'undefined' ? window.location.origin : 'https://nuskin.com')
    if (typeof window !== 'undefined' && parsed.origin === window.location.origin) {
      return true
    }
    return isHostnameTrusted(parsed.hostname)
  } catch {
    return false
  }
}

export const sanitizeSponsorId = id => {
  const value = String(id || '').trim()
  return SPONSOR_ID_PATTERN.test(value) ? value : ''
}

export const sanitizeMysiteSubdomain = value => {
  const segment = String(value || '').trim()
  return MYSITE_SUBDOMAIN_PATTERN.test(segment) ? segment : ''
}

export const sanitizePitchId = id => {
  const value = String(id || '').trim()
  return PITCH_ID_PATTERN.test(value) ? value : ''
}

/**
 * Builds a same-origin PO landing path: /{market}/{lang}/personal-offer/{sponsorId}
 */
export const buildSameOriginPoPath = ({ market, language, sponsorId, queryString }) => {
  const safeSponsorId = sanitizeSponsorId(sponsorId)
  if (!safeSponsorId || !market || !language) return null
  const path = `/${String(market).toLowerCase()}/${String(language).toLowerCase()}/personal-offer/${safeSponsorId}`
  return queryString ? `${path}?${queryString}` : path
}

/**
 * Replaces only the /mysite/{segment} path segment — not query or hash.
 */
export const replaceMysitePathSegment = ({ pathname, oldSegment, newSegment }) => {
  const safeOld = sanitizeSponsorId(oldSegment) || sanitizeMysiteSubdomain(oldSegment)
  const safeNew = sanitizeMysiteSubdomain(newSegment)
  if (!safeOld || !safeNew || !pathname) return null

  const pattern = new RegExp(`(/mysite/)${escapeRegex(safeOld)}(?=/|$)`)
  if (!pattern.test(pathname)) return null
  return pathname.replace(pattern, `$1${safeNew}`)
}

/**
 * Prepends the active locale prefix and assigns to window.location.
 * @param {string} pagePath - App-relative path, e.g. '/signup'.
 */
export const navigateWithLocale = pagePath => {
  if (typeof window === 'undefined') return
  const locale = APPConfig.getActiveAppConfig()
    ?.defaultLocale?.split('_')
    ?.reverse()
    ?.join('/')
    ?.toLowerCase()
  window.location.assign(`${locale ? `/${locale}` : ''}${pagePath}`)
}

/**
 * Sanitizes a redirect URL to prevent open redirect and XSS vulnerabilities.
 * Only allows relative URLs starting with '/'.
 */
export const sanitizeRedirectUrl = (url, fallbackUrl = '/') => {
  if (!url || typeof url !== 'string') return fallbackUrl
  const cleanUrl = url.trim().replace(/[<>"']/g, '')
  if (/^(javascript|data|vbscript):/i.test(cleanUrl)) return fallbackUrl
  if (!/^\/[a-zA-Z0-9/_-]*(\?[a-zA-Z0-9&=_%./-]*)?(#[a-zA-Z0-9_-]*)?$/.test(cleanUrl)) {
    return fallbackUrl
  }
  return cleanUrl
}

/**
 * Safely navigates to a sanitized relative URL.
 */
export const safeNavigate = (url, fallbackUrl = '/') => {
  const sanitizedUrl = sanitizeRedirectUrl(url, fallbackUrl)
  if (typeof window !== 'undefined') {
    window.location.assign(sanitizedUrl)
  }
}

/**
 * Redirect via location.replace when url is trusted; otherwise fallback.
 */
export const safeRedirect = (url, fallbackUrl = '/') => {
  if (typeof window === 'undefined') return
  const target = isTrustedRedirectUrl(url) ? url : fallbackUrl
  window.location.replace(target)
}

/**
 * Navigate via location.assign when url is trusted; otherwise fallback.
 */
export const safeAssign = (url, fallbackUrl = '/') => {
  if (typeof window === 'undefined') return
  let target = fallbackUrl
  if (typeof url === 'string' && url.startsWith('/')) {
    target = sanitizeRedirectUrl(url, fallbackUrl)
  } else if (isTrustedRedirectUrl(url)) {
    target = url
  }
  window.location.assign(target)
}
